import * as React from 'react';
import { ReactNode, useState, createContext, useContext, useMemo, useEffect } from 'react';
import { useVehicleInfo } from './vehicleInfo';
import { profileComplete } from '../utils/validation';
import { IFormMetadata } from '../types/IFormMetadata';
import { UseState } from '../types/UseStateType';
import { EVENTS } from '@kmx/io-core/offer';

interface IFormContext {
    plateDecodeError: boolean;
    setPlateDecodeError: (isPlateValid: boolean) => void;
    plateVinValid: boolean;
    setPlateVinValid: (isPlateValid: boolean) => void;
    startingMethod?: string;
    formMetadata: IFormMetadata;
    setFormMetadata: UseState<IFormMetadata>;
    zipDecodeError: string;
    setZipDecodeError: UseState<string>;
}

const FormContext = createContext<IFormContext>({} as IFormContext);

interface IFormProviderProps {
    children: ReactNode;
    startingMethod?: string;
    originPage?: string;
}

export const QS_WHITELIST = ['adcode', 'utm_content', 'utm_term'];

const filterQueryString = (queryString: string, queryStringWhiteList = QS_WHITELIST) => {
    if (!queryString) {
        return undefined;
    }

    const searchParams = new URLSearchParams(queryString);

    const newSearchParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
        if (queryStringWhiteList.findIndex(whiteList => whiteList.toLowerCase() == key.toLowerCase()) != -1) {
            newSearchParams.append(key, value);
        }
    });

    if (newSearchParams.size == 0) {
        return undefined;
    }

    return newSearchParams.toString();
};

const FormProvider: React.FC<IFormProviderProps> = ({ children, startingMethod, originPage }) => {
    const { vehicleInfo } = useVehicleInfo();
    const [plateVinValid, setPlateVinValid] = useState(profileComplete(vehicleInfo));
    const [plateDecodeError, setPlateDecodeError] = useState(false);
    const [zipDecodeError, setZipDecodeError] = useState('');
    const initialData: IFormMetadata = {
        originPage,
        querystring: filterQueryString(location.search),
        startingMethod: startingMethod === 'LP' || startingMethod === 'VIN' ? startingMethod : undefined,
    };

    useEffect(() => {
        const vehicleDecodeEndedHandler = () => {
            setPlateDecodeError(true);
        };

        const zipCodeDecodeErrorHandler = () => {
            setZipDecodeError(`We don't have enough info on car prices in that zip code to make an offer.`);
        };

        window.addEventListener(EVENTS.plateDecodeError, vehicleDecodeEndedHandler, false);
        window.addEventListener(EVENTS.zipCodeDecodeError, zipCodeDecodeErrorHandler, false);

        return () => {
            window.removeEventListener(EVENTS.plateDecodeError, vehicleDecodeEndedHandler, false);
            window.removeEventListener(EVENTS.zipCodeDecodeError, zipCodeDecodeErrorHandler, false);
        };
    }, []);

    const [formMetadata, setFormMetadata] = useState(initialData);

    useEffect(() => {
        setFormMetadata(prev => ({
            ...prev,
            originPage,
            startingMethod: startingMethod === 'LP' || startingMethod === 'VIN' ? startingMethod : undefined,
        }));
    }, [startingMethod, originPage]);

    const value = useMemo(
        () => ({
            plateVinValid,
            setPlateVinValid,
            plateDecodeError,
            setPlateDecodeError,
            startingMethod,
            formMetadata,
            setFormMetadata,
            zipDecodeError,
            setZipDecodeError,
        }),
        [plateVinValid, plateDecodeError, startingMethod, formMetadata, zipDecodeError]
    );

    return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export function useFormContext() {
    const formContext = useContext(FormContext);

    if (!formContext) {
        throw new Error('useFormContext has to be used within FormContextProvider.');
    }

    return formContext;
}

export default FormProvider;
